import { useCallback, useMemo } from "react";

import { useTranslation } from "react-i18next";

import { Button, Message, Modal, Table } from "semantic-ui-react";

import type { OpenAPI } from "simplydo/interfaces";
import { ImageWithFallback } from "simplydo/react/components/ImageWithFallback";

import api from "api";
import util from "utils/utils";

import { UserChip } from "components/lib/Chips";
import BaseChip from "components/lib/Chips/BaseChip";
import { AssessmentRoundLabel } from "components/lib/UI/AssessmentRoundLabel";

import type { LocalAssignments } from "./AssignmentDashboard";

type AssessmentReviewModalProps = {
  challenge: OpenAPI.GET<"/challenges/{id}">["response"];
  open: boolean;
  setOpen: (open: boolean) => void;
  localUserAssignments: LocalAssignments;
  localEmailAssignments: LocalAssignments;
  onSave: () => void;
};

export const AssessmentReviewModal = ({
  challenge,
  open,
  setOpen,
  localUserAssignments,
  localEmailAssignments,
  onSave,
}: AssessmentReviewModalProps) => {
  const { t } = useTranslation();
  const template = challenge?.ownerIdeaTemplate;
  const assessmentRounds = template?.assessments?.rounds || [
    { id: "legacy", name: "Assessment" },
  ];
  const assessmentRoundMap = useMemo(() => {
    return Object.fromEntries(assessmentRounds.map((r) => [r.id, r]));
  }, [assessmentRounds]);

  const localUserAssignmentList = useMemo(
    () => Object.entries(localUserAssignments),
    [localUserAssignments],
  );
  const localEmailAssignmentList = useMemo(
    () => Object.entries(localEmailAssignments),
    [localEmailAssignments],
  );

  // Saving the assessments requires transforming the local state into the api expected shape
  // The shapes are very similar but we cull the extra data such as the full user and idea objects
  const saveAssessments = useCallback(() => {
    const userAssignments = {};
    for (const [userId, userAssignment] of localUserAssignmentList) {
      for (const [ideaId, ideaAssignment] of Object.entries(
        userAssignment.ideas,
      )) {
        if (!userAssignments[userId]) {
          userAssignments[userId] = { ideas: {} };
        }
        userAssignments[userId].ideas[ideaId] = {
          assessmentRoundIds: ideaAssignment.assessmentRoundIds,
        };
      }
    }
    const emailAssignments = {};
    for (const [email, emailAssignment] of localEmailAssignmentList) {
      for (const [ideaId, ideaAssignment] of Object.entries(
        emailAssignment.ideas,
      )) {
        if (!emailAssignments[email]) {
          emailAssignments[email] = { ideas: {} };
        }
        emailAssignments[email].ideas[ideaId] = {
          assessmentRoundIds: ideaAssignment.assessmentRoundIds,
        };
      }
    }
    api.challenges.assignAssessorAssignments(
      challenge?._id,
      { userAssignments, emailAssignments },
      () => {
        api.queryClient.invalidateQueries({
          queryKey: ["challenges", challenge?._id, "assessors", "dashboard"],
        });
        api.queryClient.invalidateQueries({
          queryKey: ["challenges", challenge?._id, "ideas"],
        });
        api.queryClient.invalidateQueries({
          queryKey: ["challenges", challenge?._id, "assessments"],
        });
        onSave();
        setOpen(false);
      },
      () => {},
    );
  }, [
    challenge?._id,
    localUserAssignmentList,
    localEmailAssignmentList,
    onSave,
    setOpen,
  ]);

  return (
    <Modal
      mountNode={document.getElementById("semantic-modal-mount-node")}
      open={open}
      onClose={() => setOpen(false)}
    >
      <Modal.Header>Assignment review</Modal.Header>

      <Modal.Content scrolling>
        <Message>
          Please review the assignments below. Once you confirm, the assessors
          will be notified and invited to assess the {t("generic.ideas")}.
        </Message>
        <Table compact>
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell collapsing>User</Table.HeaderCell>
              <Table.HeaderCell>Idea</Table.HeaderCell>
              <Table.HeaderCell collapsing>Assessment name</Table.HeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {localUserAssignmentList.flatMap(([userId, assignment]) =>
              Object.values(assignment.ideas).flatMap((idea, i) =>
                idea.assessmentRoundIds.map((assessmentRoundId, j) => {
                  const round = assessmentRoundMap[assessmentRoundId];
                  return (
                    <Table.Row key={userId}>
                      <Table.Cell>
                        {i === 0 && j === 0 ?
                          <UserChip
                            user={assignment.user.ownerAssessor}
                            compact
                          />
                        : null}
                      </Table.Cell>
                      <Table.Cell>
                        <ImageWithFallback
                          avatar
                          style={{
                            minWidth: 35,
                            width: 35,
                            height: 25,
                            borderRadius: 4,
                            objectFit: "cover",
                          }}
                          src={util.ideaCoverImage(idea.idea, "200x200")}
                          fallbackSrc={util.ideaCoverImage()}
                        />
                        {idea.idea.name}
                      </Table.Cell>
                      <Table.Cell>
                        <AssessmentRoundLabel
                          key={round.id}
                          assessmentRound={round}
                        />
                      </Table.Cell>
                    </Table.Row>
                  );
                }),
              ),
            )}
            {localEmailAssignmentList.flatMap(([userId, assignment]) =>
              Object.values(assignment.ideas).flatMap((idea, i) =>
                idea.assessmentRoundIds.map((assessmentRoundId, j) => {
                  const round = assessmentRoundMap[assessmentRoundId];
                  return (
                    <Table.Row key={userId}>
                      <Table.Cell>
                        {i === 0 && j === 0 ?
                          <BaseChip
                            icon="mail"
                            title={assignment.user.email}
                            compact="very"
                            content={`This is a pending invitation, the user has not yet signed up to ${util.appName()}.`}
                          />
                        : null}
                      </Table.Cell>
                      <Table.Cell>
                        <ImageWithFallback
                          avatar
                          style={{
                            minWidth: 35,
                            width: 35,
                            height: 25,
                            borderRadius: 4,
                            objectFit: "cover",
                          }}
                          src={util.ideaCoverImage(idea.idea, "200x200")}
                          fallbackSrc={util.ideaCoverImage()}
                        />
                        {idea.idea.name}
                      </Table.Cell>
                      <Table.Cell>
                        <AssessmentRoundLabel
                          key={round.id}
                          assessmentRound={round}
                        />
                      </Table.Cell>
                    </Table.Row>
                  );
                }),
              ),
            )}
          </Table.Body>
        </Table>
      </Modal.Content>
      <Modal.Actions>
        <Button onClick={() => setOpen(false)}>Cancel</Button>
        <Button primary onClick={saveAssessments}>
          Save assignments
        </Button>
      </Modal.Actions>
    </Modal>
  );
};
